// @ts-nocheck

import { useState } from 'react';
import EmptyContainer from 'components/app-components/home/EmptyContainer';
import GenericModal from 'components/app-components/modals/GenericModal';
import LoadingSpinner from 'icons/LoadingSpinner';
import Button from 'components/app-components/ui/button';
import { useModal } from 'hooks/useModal';
import { useUser } from 'context/user';
import WalletAddMethodModal from './WalletAddMethodModal';
import { ExternalAccount } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';
import { isSupportAdminMode } from 'utils/user';
import TheraMenu from 'components/generic-components/menu/Menu';
import { useTheraFernClient } from 'hooks/useFern';
import theraToast from 'components/generic-components/toast/TheraToast';
import useReadOnlyContractor from 'hooks/useOceansContractorReadOnly';

const getDigitalCurrencyLogo = (currency: string) => {
  switch (currency) {
    case 'USDT':
      return '/images/usdt-logo.png';
    case 'USDC':
      return '/images/apps/digital_currency.png';
    default:
      return '/images/apps/digital_currency.png';
  }
};

const WalletWithdrawalMethodsList = ({
  handleSelectMethod,
  selectedMethodId,
  bridgeExternalAccounts,
  isBridgeExternalAccountsPending,
  mutateBridgeAccounts,
}: {
  handleSelectMethod: (id: string) => void;
  selectedMethodId: string | null;
  bridgeExternalAccounts: ExternalAccount[];
  isBridgeExternalAccountsPending: boolean;
  mutateBridgeAccounts: () => Promise<ExternalAccount[]>;
}) => {
  const { isLoading: isUserLoading } = useUser();
  const { theraFernApi } = useTheraFernClient();
  const addMethodModal = useModal();
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  // Read-only contractors cannot manage wallet withdrawal methods
  const { isReadOnlyContractor, readOnlyTooltipMessage } = useReadOnlyContractor();
  const isLoading = isUserLoading || isBridgeExternalAccountsPending;

  const onDelete = async (methodId: string) => {
    try {
      setIsDeleting(methodId);
      await theraFernApi.thera.bridge.bridgeApi.unlinkExternalAccount(methodId);
      await mutateBridgeAccounts();
      theraToast.success('Method deleted');
    } catch (error) {
      theraToast.error({
        message: 'Failed to delete method',
        error: error,
        options: {
          id: 'failed-to-delete-method',
        },
      });
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="w-auto rounded-lg border border-gray-200 p-4">
      <div className="mb-3 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-medium text-neutral-500">Withdrawal Methods</p>
          <Button
            variant="outline"
            onClick={addMethodModal.handleOpen}
            disabled={isSupportAdminMode || isReadOnlyContractor}
            tooltipContent={isReadOnlyContractor ? readOnlyTooltipMessage : null}
          >
            Add Method
          </Button>
        </div>
      </div>
      {isLoading ? (
        <div className="my-12 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="disabled-tooltip-container max-h-[400px] min-h-[380px] space-y-4 overflow-y-scroll pr-2">
          {bridgeExternalAccounts?.length ? (
            bridgeExternalAccounts.map((method) => {
              const isSelected = selectedMethodId === method.id;
              return (
                <div
                  key={method.id}
                  onClick={() => handleSelectMethod(method.id)}
                  role="button"
                  className={`flex w-full items-center justify-between rounded-lg border p-4 transition ${
                    isSelected ? 'border-2 border-therablue bg-[#D2D9F440] shadow-md' : 'border-gray-200 bg-white'
                  } cursor-pointer`}
                >
                  <div className="flex items-center">
                    <div
                      className={`mr-4 flex h-12 w-12 items-center justify-center rounded-full transition ${
                        isSelected ? 'bg-white' : 'bg-neutral-100'
                      }`}
                    >
                      <img src={getDigitalCurrencyLogo(method.currency?.toUpperCase())} alt="Digital Currency" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 font-medium text-neutral-800">
                        <p className="font-medium text-neutral-800">Digital Currency Transfer</p>
                        <span className="-mt-2">.</span>
                        <p className="text-xs text-neutral-900">{method.currency?.toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-400">Address: {method.walletAddress}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    {/* Read-only contractors cannot delete wallet methods */}
                    {!isReadOnlyContractor &&
                      (isDeleting === method.id ? (
                        <LoadingSpinner width={20} height={20} />
                      ) : (
                        <TheraMenu
                          nakedEllipsis
                          items={[
                            {
                              name: 'Delete',
                              handleOnClick: () => {
                                onDelete?.(method.id);
                              },
                              className: 'text-red-600',
                            },
                          ]}
                        />
                      ))}
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyContainer text="No withdrawal methods attached!" containerClass="p-4 mt-0" />
          )}
        </div>
      )}
      <GenericModal
        open={addMethodModal.isOpen}
        onClose={addMethodModal.handleClose}
        title="Add Crypto Wallet"
        showHeaderBorder={false}
        titleCenter
      >
        <WalletAddMethodModal onClose={addMethodModal.handleClose} mutateBridgeAccounts={mutateBridgeAccounts} />
      </GenericModal>
    </div>
  );
};

export default WalletWithdrawalMethodsList;
